import {faReceipt} from '@fortawesome/free-solid-svg-icons/faReceipt';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, {useState, useEffect} from 'react';
import {Col, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';
import {birthDateToAge, dateFormatFR} from '../../../utils';
import DataTable from '../../../common/components/Datatable/Datatable';
import DateRangePicker from '../../../common/components/Dates/DateRangePicker';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import SelectCivility from '../../../common/components/User/SelectCivility';
import TranslatedCivility from '../../../common/components/User/TranslatedCivility';
import {
  CIVILITIES,
  FILTER_EXPRESSION_OPERATOR,
  FILTER_OPERATOR,
  ORDER_DIRECTION,
} from '../../../common/constants';
import TicketRowActionMerchant from './TicketRowActionMerchant';
import {withRouter} from 'react-router';
import {flowRight as compose} from 'lodash';
import SelectCheckout from '../../../common/components/SelectCheckout';


const Span = styled.span`
  font-family: 'Segoe UI Bold', sans-serif;
`;

const MargedDiv = styled.div`
  margin: 5px;
`;

const SelectCheckoutContainer = styled.div`
  margin: 5px;
  width: 200px;
`;

const displayAge = (t, date) => {
  const age = birthDateToAge(date);
  if (!age) {
    return null;
  }
  return t('user.yearsold', {age});
};

const GRAPHQL_QUERY_RECEIPTS = gql`
    query receipts($criteria:CriteriaInput){
        receipts(criteria: $criteria){
            rows{
                id
                isDeleted
                isFirstOrder
                userHasLoyaltyCard
                rating
                createdAt
                file {
                    id
                    token
                    filename
                    mimeType
                }
                comments {
                    id
                    review
                }
                user{
                    id
                    barcode
                    birthDate
                    civility
                    email
                    firstName
                    lastName
                    postalCode
                }
                brand{
                    id
                    name
                    createdAt
                    updatedAt
                }
                checkout{
                    id
                    name
                }
                tags {
                    id
                    rows {
                        id
                        name
                    }
                }
            }
            total
            id
        }
    }
`;


const columns = t => [
  {
    Header: '#',
    accessor: 'id',
    sortable: true,
  },
  {
    Header: t('dataTable.creation'),
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
    accessor: 'createdAt',
    sortable: true,
  },
  {
    Header: t('user.ident'),
    Cell: ({cell}) => cell.value || '',
    accessor: 'user.barcode',
    sortable: true,
  },
  {
    Header: t('user.postalCode'),
    Cell: ({cell}) => cell.value || '',
    accessor: 'user.postalCode',
    sortable: true,
  },
  {
    Header: t('dataTable.checkout'),
    Cell: ({cell}) => cell.value || '',
    accessor: 'checkout.name',
  },
  {
    Header: t('user.genre'),
    Cell: ({cell}) => <TranslatedCivility>{cell.value}</TranslatedCivility>,
    accessor: 'user.civility',
  },
  {
    Header: t('user.age'),
    Cell: ({cell}) => displayAge(t, cell.value),
    accessor: 'user.birthDate',
    sortable: true,
  },
  {
    Header: t('dataTable.detail'),
    Cell: ({cell}) => <TicketRowActionMerchant receipt={cell.value}/>,
    accessor: row => row,
  },
];

const createFilters = (search, checkoutFilter, civilityFilter, startDate, endDate) => {
  const andFilters = [];
  const startObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.GreaterThan,
    value: startDate,
  };
  const endObject = {
    field: 'receipt.createdAt',
    operator: FILTER_OPERATOR.LessThan,
    value: endDate,
  };

  const civilityObject = {
    field: 'user.civility',
    operator: FILTER_OPERATOR.Equal,
    value: civilityFilter,
  };

  const checkoutObject = {
    field: 'checkout.id',
    operator: FILTER_OPERATOR.Equal,
    value: checkoutFilter,
  };

  if (startDate) {
    andFilters.push(startObject);
  }

  if (endDate) {
    andFilters.push(endObject);
  }

  if (Object.values(CIVILITIES).includes(civilityFilter)) {
    andFilters.push(civilityObject);
  }

  if (checkoutFilter) {
    andFilters.push(checkoutObject);
  }

  const expressions = [{
    operator: FILTER_EXPRESSION_OPERATOR.And,
    filters: andFilters,
    expressions: {
      operator: FILTER_EXPRESSION_OPERATOR.Or,
      filters: [{
        field: 'receipt.id',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }, {
        field: 'user.barcode',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }, {
        field: 'user.postalCode',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }, {
        field: 'user.mobilePhone',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }, {
        field: 'receipt.mobilePhone',
        operator: FILTER_OPERATOR.Like,
        value: `%${search}%`,
      }],
    },
  }];

  return {
    expressions,
    relations: ['user', 'brand', 'file', 'checkout'],
  };
};

const TicketViewMerchant = ({t, location}) => {
  const [search, setSearch] = useState('');
  const [civilityFilter, setCivilityFilter] = useState('default');
  const [{startDate, endDate}, setDateFilter] = useState({});
  const [checkoutFilter, setCheckoutFilter] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutId = params.get('checkout');
    setCheckoutFilter(checkoutId);
  }, [location]);

  const additionalFilters = (
    <>
      <MargedDiv>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDatesChange={({startDate, endDate}) => setDateFilter({startDate, endDate})}/>
      </MargedDiv>
      <MargedDiv>
        <SelectCivility value={civilityFilter} allowNull onChange={e => setCivilityFilter(e.target.value)}/>
      </MargedDiv>
      <SelectCheckoutContainer>
        <SelectCheckout
          lightStyles={true}
          onChange={option => {
            if (option && option.value) {
              setCheckoutFilter(option.value);
            } else {
              setCheckoutFilter('');
            }
          }}
          initialSelectedValues={[checkoutFilter]}
          isClearable={true}/>
      </SelectCheckoutContainer>
    </>
  );

  return (
    <Screen
      header={<Metadata title={t('title')} meta={t('meta')}/>}
      leftTitle={
        <>
          <FontAwesomeIcon icon={faReceipt} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('tickets')}</Span>
        </>
      }>
      <Row>
        <Col xs={12}>
          <DataTable
            query={GRAPHQL_QUERY_RECEIPTS}
            columns={columns(t)}
            keyResult="receipts"
            defaultSort={{field: 'createdAt', direction: ORDER_DIRECTION.DESC}}
            onSearch={value => setSearch(value)}
            advancedFilters={createFilters(search, checkoutFilter, civilityFilter, startDate, endDate)}
            additionalFilters={additionalFilters}/>
        </Col>
      </Row>
    </Screen>
  );
};

TicketViewMerchant.propTypes = {
  t: PropTypes.func.isRequired,
};

export default compose(
  withRouter,
  withTranslation('ticket'),
)(TicketViewMerchant);
